/**
 * ScriptAnalyzerService.js
 * Client-side service for analyzing JavaScript and script issues on websites
 */

import htmlFetcherService from './HtmlFetcherService';

class ScriptAnalyzerService {
  constructor() {
    this.scriptCache = new Map();
    this.maxConcurrent = 5;
    this.activeRequests = 0;
    
    // Get API URL from environment or fallback to default
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.proxyUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    
    // Common script types and their descriptions
    this.scriptTypes = {
      'analytics': {
        patterns: [/analytics|gtag|ga\.js|googletagmanager|segment|heap|mixpanel|amplitude|plausible|matomo|piwik/i],
        description: 'Analytics script',
        impact: 'low',
        recommendations: [
          'Consider loading analytics scripts with the "async" attribute',
          'Ensure privacy compliance (GDPR, CCPA) with proper user consent',
          'Review if all analytics events are necessary'
        ]
      },
      'advertising': {
        patterns: [/adserv|adsystem|adroll|doubleclick|googlesyndication|ad-|pagead|banner|advert/i],
        description: 'Advertising script',
        impact: 'high',
        recommendations: [
          'Consider lazy loading ad scripts after page load',
          'Consolidate multiple ad providers when possible',
          'Implement ads.txt to prevent unauthorized inventory sales'
        ]
      },
      'social': {
        patterns: [/facebook|twitter|linkedin|instagram|pinterest|youtube|tiktok|social/i],
        description: 'Social media script',
        impact: 'medium',
        recommendations: [
          'Load social widgets only after user interaction',
          'Use lightweight social sharing alternatives',
          'Consider using static links instead of JavaScript widgets'
        ]
      },
      'tracking': {
        patterns: [/track|pixel|beacon|tag-manager|hotjar|clarity|mouseflow|crazyegg/i],
        description: 'Tracking/monitoring script',
        impact: 'medium',
        recommendations: [
          'Consolidate tracking tools to reduce overhead',
          'Ensure user consent before loading tracking scripts',
          'Consider sampling for non-critical user monitoring'
        ]
      },
      'jquery': {
        patterns: [/jquery/i],
        description: 'jQuery library',
        impact: 'medium',
        recommendations: [
          'Consider using native JavaScript methods instead of jQuery',
          'If jQuery is necessary, use a modern slim version',
          'Load from a reliable CDN with cache headers'
        ]
      },
      'bootstrap': {
        patterns: [/bootstrap/i],
        description: 'Bootstrap framework',
        impact: 'medium',
        recommendations: [
          'Use only required Bootstrap components instead of the entire library',
          'Consider using Bootstrap\'s CSS-only version if JavaScript features aren\'t needed',
          'Update to the latest version for better performance'
        ]
      },
      'react': {
        patterns: [/react(-dom)?/i],
        description: 'React library',
        impact: 'medium',
        recommendations: [
          'Ensure you\'re using production build (not development)',
          'Consider code splitting for large React applications',
          'Implement React.lazy() for component-level code splitting'
        ]
      },
      'vue': {
        patterns: [/vue/i],
        description: 'Vue.js framework',
        impact: 'medium',
        recommendations: [
          'Ensure you\'re using production build (not development)',
          'Consider async components for large Vue applications',
          'Use Vue CLI\'s modern mode for modern browsers'
        ]
      },
      'angular': {
        patterns: [/angular/i],
        description: 'Angular framework',
        impact: 'high',
        recommendations: [
          'Use Ahead-of-Time (AOT) compilation',
          'Implement lazy loading for feature modules',
          'Enable production mode to disable development-specific code'
        ]
      },
      'chatbot': {
        patterns: [/chat|intercom|zendesk|drift|tawk|livechat|freshchat|crisp|tidio/i],
        description: 'Chat/support script',
        impact: 'high',
        recommendations: [
          'Load chat widgets only after page is fully loaded',
          'Consider loading chat widgets on user interaction',
          'Evaluate if chat functionality is needed on all pages'
        ]
      }
    };
  }

  /**
   * Analyze scripts on a URL
   * @param {string} url - The URL to analyze
   * @returns {Promise<Object>} - Analysis results
   */
  async analyzeScripts(url) {
    try {
      console.log(`[ScriptAnalyzerService] Analyzing scripts for ${url}`);
      
      // Extract scripts using the proxy to avoid CORS
      const scripts = await this._extractScripts(url);
      
      console.log(`[ScriptAnalyzerService] Found ${scripts.length} scripts to analyze`);
      
      // Analyze script loading patterns
      const loadingIssues = this._analyzeScriptLoading(scripts);
      
      // Analyze script sizes
      const sizeIssues = await this._analyzeScriptSizes(scripts);
      
      // Analyze third-party scripts
      const thirdPartyIssues = this._analyzeThirdPartyScripts(scripts, url);
      
      // Collect all issues
      const allIssues = [
        ...loadingIssues,
        ...sizeIssues,
        ...thirdPartyIssues
      ];
      
      // Generate overall recommendations based on findings
      const recommendations = this._generateOverallRecommendations(allIssues);
      
      return {
        url,
        issues: allIssues,
        totalIssues: allIssues.length,
        recommendations,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`[ScriptAnalyzerService] Error analyzing scripts: ${error.message}`);
      throw error;
    }
  }

  /**
   * Extract scripts from a URL (wrapper for _extractScriptsFromUrl)
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Extracted scripts
   */
  async _extractScripts(url) {
    return this._extractScriptsFromUrl(url);
  }

  /**
   * Extract scripts from HTML content of a URL
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Extracted scripts
   */
  async _extractScriptsFromUrl(url) {
    try {
      console.log(`[ScriptAnalyzerService] Fetching HTML content for ${url}`);
      
      // Use the shared HTML fetcher service
      const html = await htmlFetcherService.fetchHtml(url);
      
      console.log(`[ScriptAnalyzerService] Successfully fetched HTML (${html.length} bytes)`);
      return this._extractScriptsFromHtml(html, url);
    } catch (error) {
      console.error('Error extracting scripts:', error.message);
      
      // Try direct fetch as fallback
      try {
        console.log(`[ScriptAnalyzerService] Trying direct fetch for ${url}`);
        const response = await fetch(url);
        if (response.ok) {
          const html = await response.text();
          console.log(`[ScriptAnalyzerService] Direct fetch successful (${html.length} bytes)`);
          return this._extractScriptsFromHtml(html, url);
        }
      } catch (directError) {
        console.warn('Direct fetch failed:', directError.message);
      }
      
      throw new Error(`Failed to extract scripts: ${error.message}`);
    }
  }

  /**
   * Extract scripts from HTML content
   * @private
   * @param {string} html - HTML content
   * @param {string} baseUrl - Base URL for resolving relative script URLs
   * @returns {Array} - Extracted scripts
   */
  _extractScriptsFromHtml(html, baseUrl) {
    try {
      // Create a DOM parser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get all script elements
      const scriptElements = doc.querySelectorAll('script');
      const scripts = [];
      
      // Extract information about each script
      scriptElements.forEach(script => {
        const scriptSrc = script.getAttribute('src');
        const scriptType = script.getAttribute('type') || 'text/javascript';
        const scriptContent = script.textContent;
        const isInline = !scriptSrc;
        
        // Only process JavaScript (skip JSON, module declarations, etc.)
        if (scriptType === 'text/javascript' || 
            scriptType === 'application/javascript' || 
            !scriptType || 
            scriptType === 'module') {
          
          let scriptUrl = null;
          if (scriptSrc) {
            try {
              scriptUrl = new URL(scriptSrc, baseUrl).href;
            } catch (urlError) {
              console.warn(`Invalid script URL: ${scriptSrc}`);
              scriptUrl = scriptSrc; // Keep the original even if invalid
            }
          }
          
          const scriptObj = {
            url: scriptUrl,
            isAsync: script.hasAttribute('async'),
            isDefer: script.hasAttribute('defer'),
            isModule: script.getAttribute('type') === 'module',
            isInline,
            inlineSize: isInline ? scriptContent.length : 0,
            position: script.parentNode?.tagName || 'unknown',
            inHead: !!script.closest('head'),
            content: isInline && scriptContent.length < 500 ? scriptContent : null
          };
          
          scripts.push(scriptObj);
        }
      });
      
      console.log(`[ScriptAnalyzerService] Extracted ${scripts.length} scripts from HTML`);
      return scripts;
    } catch (error) {
      console.error('Error in HTML script extraction:', error.message);
      return [];
    }
  }

  /**
   * Analyze script loading issues
   * @private
   * @param {Array} scripts - Array of script objects
   * @returns {Array} - Loading issues
   */
  _analyzeScriptLoading(scripts) {
    const issues = [];
    
    // Check for render-blocking scripts
    const renderBlockingScripts = scripts.filter(script => 
      !script.isAsync && !script.isDefer && !script.isModule && script.url && script.inHead
    );
    
    if (renderBlockingScripts.length > 0) {
      issues.push({
        type: 'render-blocking-scripts',
        url: 'multiple-scripts',
        description: `${renderBlockingScripts.length} render-blocking scripts found in <head>`,
        details: renderBlockingScripts.map(script => script.url),
        impact: renderBlockingScripts.length > 5 ? 'high' : renderBlockingScripts.length > 2 ? 'medium' : 'low',
        recommendations: [
          'Add async or defer attributes to non-critical scripts',
          'Move non-critical scripts to the end of the body',
          'Consider using resource hints like preload for critical scripts'
        ]
      });
    }
    
    // Check for large inline scripts
    const largeInlineScripts = scripts.filter(script => 
      script.isInline && script.inlineSize > 5000
    );
    
    if (largeInlineScripts.length > 0) {
      issues.push({
        type: 'large-inline-scripts',
        url: 'inline-scripts',
        description: `${largeInlineScripts.length} large inline scripts found (>5KB)`,
        impact: 'medium',
        recommendations: [
          'Move large inline scripts to external files that can be cached',
          'Consider breaking large scripts into smaller modules',
          'Minify inline JavaScript to reduce size'
        ]
      });
    }
    
    // Check for duplicate jQuery or common libraries
    const scriptUrls = scripts.filter(script => script.url).map(script => script.url);
    const jqueryScripts = scriptUrls.filter(url => url.includes('jquery'));
    
    if (jqueryScripts.length > 1) {
      issues.push({
        type: 'duplicate-library',
        url: jqueryScripts[0],
        description: `Multiple jQuery instances detected (${jqueryScripts.length})`,
        details: jqueryScripts,
        impact: 'high',
        recommendations: [
          'Remove duplicate jQuery instances',
          'Ensure all plugins use the same jQuery instance',
          'Consider using vanilla JavaScript instead'
        ]
      });
    }
    
    return issues;
  }

  /**
   * Analyze third-party script issues
   * @private
   * @param {Array} scripts - Array of script objects
   * @param {string} pageUrl - URL of the page being analyzed
   * @returns {Array} - Third-party script issues
   */
  _analyzeThirdPartyScripts(scripts, pageUrl) {
    const issues = [];
    const pageHostname = new URL(pageUrl).hostname;
    
    // Count scripts by domain
    const scriptsByDomain = {};
    scripts.filter(script => script.url).forEach(script => {
      try {
        const scriptHost = new URL(script.url).hostname;
        if (scriptHost !== pageHostname) {
          if (!scriptsByDomain[scriptHost]) {
            scriptsByDomain[scriptHost] = [];
          }
          scriptsByDomain[scriptHost].push(script.url);
        }
      } catch (error) {
        // Skip invalid URLs
      }
    });
    
    // Identify domains with too many scripts
    for (const [domain, urls] of Object.entries(scriptsByDomain)) {
      if (urls.length >= 3) {
        issues.push({
          type: 'multiple-third-party-scripts',
          url: urls[0],
          description: `Multiple scripts (${urls.length}) from third-party domain: ${domain}`,
          details: urls,
          impact: urls.length > 5 ? 'high' : 'medium',
          recommendations: [
            'Consolidate requests to the same third-party domain',
            'Consider if all scripts from this domain are necessary',
            'Use a single loader script if possible'
          ]
        });
      }
      
      // Identify script types
      for (const [scriptType, info] of Object.entries(this.scriptTypes)) {
        const matchingUrls = urls.filter(url => 
          info.patterns.some(pattern => pattern.test(url))
        );
        
        if (matchingUrls.length > 0) {
          issues.push({
            type: `third-party-${scriptType}`,
            url: matchingUrls[0],
            description: `${info.description} detected from ${domain}`,
            details: matchingUrls,
            impact: info.impact,
            recommendations: info.recommendations
          });
        }
      }
    }
    
    return issues;
  }

  /**
   * Analyze script sizes
   * @private
   * @param {Array} scripts - Array of script objects
   * @returns {Promise<Array>} - Issues found
   */
  async _analyzeScriptSizes(scripts) {
    const issues = [];
    const scriptUrls = scripts.filter(script => script.url);
    
    if (scriptUrls.length === 0) {
      return issues;
    }

    try {
      // Send all script URLs in one batch request
      const response = await fetch(`${this.proxyUrl}/pagespeed/resource-sizes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          urls: scriptUrls.map(script => script.url)
        })
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      const scriptSizes = data.sizes || {};
      
      // Process each script's size
      for (const script of scriptUrls) {
        const size = scriptSizes[script.url] || 0;
        
        // Categorize script by size
        let sizeCategory = 'normal';
        let impact = 'low';
        let recommendations = [];
        
        if (size > 500 * 1024) { // Over 500KB
          sizeCategory = 'very-large';
          impact = 'high';
          recommendations = [
            'Split into multiple smaller files with code splitting',
            'Use tree shaking to remove unused code',
            'Consider lazy loading this script only when needed'
          ];
        } else if (size > 200 * 1024) { // Over 200KB
          sizeCategory = 'large';
          impact = 'medium';
          recommendations = [
            'Minify and compress the script',
            'Consider using a smaller alternative library',
            'Check for unused features that could be removed'
          ];
        } else if (size > 100 * 1024) { // Over 100KB
          sizeCategory = 'medium-large';
          impact = 'low';
          recommendations = [
            'Ensure the script is properly minified',
            'Use gzip or brotli compression on the server'
          ];
        }
        
        // Detect library type
        let detectedType = null;
        for (const [type, info] of Object.entries(this.scriptTypes)) {
          if (info.patterns.some(pattern => pattern.test(script.url))) {
            detectedType = type;
            if (info.recommendations && recommendations.length === 0) {
              recommendations = info.recommendations;
            }
            break;
          }
        }
        
        // Create issue if script is large
        if (sizeCategory !== 'normal') {
          issues.push({
            type: `${sizeCategory}-script`,
            url: script.url,
            size,
            description: detectedType 
              ? `Large ${this.scriptTypes[detectedType].description} (${Math.round(size / 1024)} KB)`
              : `Large JavaScript file detected (${Math.round(size / 1024)} KB)`,
            impact,
            recommendations,
            fileType: detectedType || 'unknown'
          });
        }
        
        // Store in cache
        this.scriptCache.set(script.url, { 
          size, 
          type: detectedType,
          issue: sizeCategory !== 'normal' ? issues[issues.length - 1] : null
        });
      }
      
      return issues;
    } catch (error) {
      console.error('Error analyzing script sizes:', error);
      return [];
    }
  }
  
  /**
   * Generate overall recommendations based on all issues
   * @private
   * @param {Array} issues - All detected issues
   * @returns {Array} - Overall recommendations
   */
  _generateOverallRecommendations(issues) {
    const recommendations = new Set();
    
    // Count issue types
    const issueCounts = issues.reduce((counts, issue) => {
      const baseType = issue.type.split('-')[0];
      counts[baseType] = (counts[baseType] || 0) + 1;
      return counts;
    }, {});
    
    // Add general recommendations based on issue counts
    if (issueCounts.render || issueCounts.large) {
      recommendations.add('Implement resource hints like preload, preconnect for critical resources');
    }
    
    if (issues.some(i => i.impact === 'high')) {
      recommendations.add('Prioritize fixing high impact issues that directly affect page loading speed');
    }
    
    if (issueCounts.third && issueCounts.third > 3) {
      recommendations.add('Evaluate the necessity of each third-party script and consider removing non-essential ones');
      recommendations.add('Implement a tag manager to better control third-party script loading');
    }
    
    if (issueCounts.duplicate) {
      recommendations.add('Remove duplicate script references to prevent redundant downloads');
    }
    
    // Add specific recommendations for common scenarios
    if (issues.some(i => i.type.includes('jquery'))) {
      recommendations.add('Consider replacing jQuery with vanilla JavaScript for better performance');
    }
    
    if (issues.some(i => i.type.includes('analytics'))) {
      recommendations.add('Consolidate analytics tools to reduce overhead');
    }
    
    if (issues.some(i => i.type.includes('advertising'))) {
      recommendations.add('Lazy load ad scripts after page content has loaded');
    }
    
    // Always add these best practices
    recommendations.add('Minify and compress all JavaScript files');
    recommendations.add('Consider implementing a service worker for script caching');
    
    return Array.from(recommendations);
  }
}

// Create a singleton instance
const scriptAnalyzerService = new ScriptAnalyzerService();
export default scriptAnalyzerService; 